import React, { useState, useEffect, useContext } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Collapse,
} from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import MenuService from '../../service/menu.service';
import OrderService from '../../service/order.service';
import TableService from '../../service/table.service';
import { formatCentsToBRL, parseBRLToCents, getActiveSizes, formatItemPrice, itemHasMultipleSizes, formatVariants } from '../../utils/currency';
import { normalizePhone } from '../../utils/phone';
import { useNotification } from '../../contexts/NotificationContext';
import { ApiContext } from '../../contexts/ApiContext';
import { accountPath } from '../../utils/accountPath';
import { getMpMethodLabel, isOnlinePayment, isOrderPaid } from '../../utils/orderPayment';
import { getOrderSourceLabel } from '../../utils/orderSource';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal/ConfirmDeleteModal';

const EDIT_WINDOW_MS = 48 * 60 * 60 * 1000;

const ACTIVITY_LABELS = {
  STATUS: 'alterou o status',
  EDIT: 'editou o pedido',
  RESOLVE: 'resolveu o pedido',
  CREATE: 'criou o pedido',
};

const EditOrder = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { account } = useContext(ApiContext);
  const { notify } = useNotification();
  
  // Menu data
  const [menu, setMenu] = useState([]);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(true);
  const [openCategories, setOpenCategories] = useState({});
  
  // Order form
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [payment, setPayment] = useState('CASH');
  const [orderType, setOrderType] = useState('DELIVERY');
  const [tableId, setTableId] = useState('');
  const [tables, setTables] = useState([]);
  const [deliveryCents, setDeliveryCents] = useState(0);
  const [deliveryInput, setDeliveryInput] = useState(formatCentsToBRL(0));
  const [orderNote, setOrderNote] = useState('');
  
  // Address
  const [street, setStreet] = useState('');
  const [number, setNumber] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [complement, setComplement] = useState('');
  const [loadingCep, setLoadingCep] = useState(false);
  
  // Cart
  const [cart, setCart] = useState([]);
  const [createdAt, setCreatedAt] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [activities, setActivities] = useState([]);
  const [orderSource, setOrderSource] = useState(null);
  const [mpPaymentInfo, setMpPaymentInfo] = useState(null);

  const canEdit = createdAt ? (Date.now() - new Date(createdAt).getTime()) < EDIT_WINDOW_MS : true;
  
  // UI
  const [saving, setSaving] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [itemNote, setItemNote] = useState('');
  const [showConfirmExit, setShowConfirmExit] = useState(false);

  useEffect(() => {
    if (account?.menuCode) {
      loadMenu();
    }
    if (account?.id && account?.tablesEnabled) {
      TableService.list(account.id).then((data) => setTables(data || [])).catch(() => {});
    }
  }, [account]);

  useEffect(() => {
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  const loadOrder = async () => {
    setLoadingOrder(true);
    try {
      const order = await OrderService.getOrderById(orderId);
      setCreatedAt(order.createdAt || null);
      setUpdatedAt(order.updatedAt || null);
      setActivities(order.activities || []);
      setOrderSource(order);
      setCustomerName(order.customerName || '');
      setCustomerPhone(order.customerPhone || '');
      setPayment(order.payment || 'CASH');
      setMpPaymentInfo(isOnlinePayment(order) ? {
        paid: isOrderPaid(order),
        paidAt: order.paidAt,
        transactionId: order.mercadoPagoPaymentId,
        method: order.mpPaymentMethod,
        payerName: order.mpPayerName,
        payerEmail: order.mpPayerEmail,
      } : null);
      setOrderType(order.type || 'DELIVERY');
      setTableId(order.tableId ? String(order.tableId) : '');
      setDeliveryCents(order.deliveryCents || 0);
      setDeliveryInput(formatCentsToBRL(order.deliveryCents || 0));
      setOrderNote(order.note || '');
      
      // Preenche endereço
      if (order.address) {
        setStreet(order.address.street || '');
        setNumber(order.address.number || '');
        setNeighborhood(order.address.neighborhood || '');
        setCity(order.address.city || '');
        setState(order.address.state || '');
        setZipCode(order.address.zipCode || '');
        setComplement(order.address.complement || '');
      }
      
      // Preenche carrinho com os itens do pedido
      if (order.items && order.items.length > 0) {
        const cartItems = order.items.map((item, index) => ({
          id: item.id || Date.now() + index,
          menuItemId: item.menuItemId,
          menuItemSizeId: item.menuItemSizeId,
          sizeName: item.sizeName,
          name: item.name,
          description: item.description,
          unitPriceCents: item.unitPriceCents,
          quantity: item.quantity,
          lineTotalCents: item.lineTotalCents,
          note: item.note || '',
          variants: item.variants || [],
        }));
        setCart(cartItems);
      }
    } catch (err) {
      console.error(err);
      notify('tr', 'danger', 'Erro ao carregar pedido', 'icon-alert-circle-exc');
      navigate(accountPath(account?.id, '/pedidos'));
    } finally {
      setLoadingOrder(false);
    }
  };

  const loadMenu = async () => {
    if (!account?.menuCode) {
      notify('tr', 'danger', 'Menu code não encontrado', 'icon-alert-circle-exc');
      return;
    }
    
    setLoadingMenu(true);
    try {
      const data = await MenuService.getMenu(account.menuCode);
      setMenu(data || []);
    } catch (err) {
      console.error(err);
      notify('tr', 'danger', 'Erro ao carregar cardápio', 'icon-alert-circle-exc');
    } finally {
      setLoadingMenu(false);
    }
  };

  const handleCepChange = async (value) => {
    const cleanCep = value.replace(/\D/g, '');
    
    let formattedCep = cleanCep;
    if (cleanCep.length > 5) {
      formattedCep = cleanCep.slice(0, 5) + '-' + cleanCep.slice(5, 8);
    }
    
    setZipCode(formattedCep);
    
    if (cleanCep.length === 8) {
      setLoadingCep(true);
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
        const data = await response.json();
        
        if (data.erro) {
          notify('tr', 'warning', 'CEP não encontrado', 'icon-alert-circle-exc');
        } else {
          setStreet(data.logradouro || '');
          setNeighborhood(data.bairro || '');
          setCity(data.localidade || '');
          setState(data.uf || '');
          setComplement(data.complemento || '');
        }
      } catch (err) {
        console.error('Erro ao buscar CEP:', err);
        notify('tr', 'danger', 'Erro ao buscar CEP', 'icon-alert-circle-exc');
      } finally {
        setLoadingCep(false);
      }
    }
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => {
      const variantsTotal = item.variants.reduce((s, v) => s + (v.extraCents || 0), 0);
      return sum + (item.unitPriceCents * item.quantity) + (variantsTotal * item.quantity);
    }, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + deliveryCents;
  };

  const handleAddToCart = () => {
    if (!selectedItem || !selectedSize) return;

    const variantsTotal = selectedVariants.reduce((s, v) => s + (v.extraCents || 0), 0);
    const lineTotalCents = (selectedSize.priceCents + variantsTotal) * quantity;

    const cartItem = {
      id: Date.now(),
      menuItemId: selectedItem.id,
      menuItemSizeId: selectedSize.id,
      sizeName: itemHasMultipleSizes(selectedItem) ? selectedSize.name : undefined,
      name: selectedItem.name,
      description: selectedItem.description,
      unitPriceCents: selectedSize.priceCents,
      quantity,
      lineTotalCents,
      note: itemNote,
      variants: selectedVariants.map((v) => ({
        itemVariantId: v.id,
        name: v.name,
        extraCents: v.extraCents,
      })),
    };

    setCart([...cart, cartItem]);
    clearItemSelection();
  };

  const clearItemSelection = () => {
    setSelectedItem(null);
    setSelectedSize(null);
    setSelectedVariants([]);
    setQuantity(1);
    setItemNote('');
  };

  const handleSelectItem = (menuItem) => {
    const activeSizes = getActiveSizes(menuItem);
    setSelectedItem(menuItem);
    setSelectedSize(!itemHasMultipleSizes(menuItem) ? activeSizes[0] : null);
    setSelectedVariants([]);
  };

  const handleRemoveFromCart = (cartItemId) => {
    setCart(cart.filter(item => item.id !== cartItemId));
  };

  const handleVariantToggle = (variant) => {
    const exists = selectedVariants.find(v => v.id === variant.id);
    if (exists) {
      setSelectedVariants(selectedVariants.filter(v => v.id !== variant.id));
    } else {
      setSelectedVariants([...selectedVariants, variant]);
    }
  };

  const toggleCategory = (categoryId) => {
    setOpenCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const hasFormData = () => {
    return true; // Sempre tem dados ao editar
  };

  const handleGoBack = () => {
    setShowConfirmExit(true);
  };

  const confirmExit = () => {
    setShowConfirmExit(false);
    navigate(accountPath(account?.id, '/pedidos'));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canEdit) {
      notify('tr', 'warning', 'Edição bloqueada: o pedido tem mais de 48 horas', 'icon-alert-circle-exc');
      return;
    }

    if (!customerName.trim()) {
      notify('tr', 'warning', 'Nome do cliente é obrigatório', 'icon-alert-circle-exc');
      return;
    }
    if (!customerPhone.trim()) {
      notify('tr', 'warning', 'Telefone/WhatsApp do cliente é obrigatório', 'icon-alert-circle-exc');
      return;
    }
    if (cart.length === 0) {
      notify('tr', 'warning', 'Adicione pelo menos um item ao pedido', 'icon-alert-circle-exc');
      return;
    }

    setSaving(true);
    try {
      const selectedTable = orderType === 'DINE_IN' && tableId
        ? tables.find((t) => String(t.id) === String(tableId))
        : null;

      const orderData = {
        customerName: customerName.trim(),
        customerPhone: normalizePhone(customerPhone),
        payment,
        type: orderType,
        tableId: selectedTable ? selectedTable.id : null,
        tableName: selectedTable ? selectedTable.name : null,
        subtotalCents: calculateSubtotal(),
        deliveryCents: orderType === 'DELIVERY' ? deliveryCents : 0,
        totalCents: calculateSubtotal() + (orderType === 'DELIVERY' ? deliveryCents : 0),
        note: orderNote.trim() || null,
        items: cart.map(item => ({
          menuItemId: item.menuItemId,
          menuItemSizeId: item.menuItemSizeId,
          sizeName: item.sizeName,
          name: item.name,
          description: item.description,
          unitPriceCents: item.unitPriceCents,
          quantity: item.quantity,
          note: item.note,
          variants: item.variants,
        })),
        address: orderType === 'DELIVERY' && street.trim() ? {
          street: street.trim(),
          number: number.trim(),
          neighborhood: neighborhood.trim(),
          city: city.trim(),
          state: state.trim(),
          zipCode: zipCode.trim(),
          complement: complement.trim(),
        } : undefined,
      };

      await OrderService.updateOrder(orderId, orderData);
      notify('tr', 'success', 'Pedido atualizado com sucesso!', 'icon-check-2');
      navigate(accountPath(account?.id, '/pedidos'));
    } catch (err) {
      console.error(err);
      notify('tr', 'danger', 'Erro ao atualizar pedido', 'icon-alert-circle-exc');
    } finally {
      setSaving(false);
    }
  };

  if (loadingOrder) {
    return (
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              <CardBody>
                <div className="text-center py-5">
                  <p className="text-muted">Carregando pedido...</p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <div className="d-flex justify-content-between align-items-center">
                <CardTitle tag="h4">Editar Pedido #{orderId}</CardTitle>
                <Button color="secondary" size="sm" onClick={handleGoBack}>
                  <i className="tim-icons icon-minimal-left" /> Voltar
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              <Row>
                {/* Customer Info & Address */}
                <Col lg="4" md="12">
                  <Card className="card-plain">
                    <CardHeader>
                      <CardTitle tag="h5">Informações do Cliente</CardTitle>
                    </CardHeader>
                    <CardBody>
                      <FormGroup>
                        <Label>Nome *</Label>
                        <Input
                          type="text"
                          value={customerName}
                          onChange={(e) => setCustomerName(e.target.value)}
                          placeholder="Nome do cliente"
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Telefone/WhatsApp *</Label>
                        <Input
                          type="text"
                          value={customerPhone}
                          onChange={(e) => setCustomerPhone(e.target.value)}
                          placeholder="(00) 00000-0000"
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Forma de Pagamento *</Label>
                        <Input
                          type="select"
                          value={payment}
                          onChange={(e) => setPayment(e.target.value)}
                        >
                          <option value="CASH">Dinheiro</option>
                          <option value="CARD">Cartão</option>
                          <option value="PIX">PIX</option>
                          <option value="ONLINE">Pagamento virtual</option>
                        </Input>
                        {mpPaymentInfo && (
                          <div style={{ marginTop: 8, fontSize: 13, color: '#aaa' }}>
                            <span style={{
                              fontSize: 11,
                              fontWeight: 700,
                              padding: '3px 8px',
                              borderRadius: 4,
                              background: mpPaymentInfo.paid ? 'rgba(0, 187, 51, 0.2)' : 'rgba(255, 193, 7, 0.2)',
                              color: mpPaymentInfo.paid ? '#4ade80' : '#ffc107',
                            }}>
                              {mpPaymentInfo.paid ? 'PAGO' : 'AGUARDANDO PAGAMENTO'}
                            </span>
                            {mpPaymentInfo.paid && mpPaymentInfo.transactionId && (
                              <div style={{ marginTop: 6, fontFamily: 'monospace', fontSize: 12 }}>
                                ID transação MP: {mpPaymentInfo.transactionId}
                              </div>
                            )}
                            {mpPaymentInfo.method && (
                              <div style={{ marginTop: 4 }}>Método: {getMpMethodLabel(mpPaymentInfo.method)}</div>
                            )}
                            {mpPaymentInfo.payerName && (
                              <div style={{ marginTop: 4 }}>Pagador: {mpPaymentInfo.payerName}</div>
                            )}
                          </div>
                        )}
                      </FormGroup>
                      <FormGroup>
                        <Label>Tipo de Pedido *</Label>
                        <Input
                          type="select"
                          value={orderType}
                          onChange={(e) => setOrderType(e.target.value)}
                        >
                          <option value="DELIVERY">Entrega</option>
                          <option value="PICKUP">Retirada</option>
                          <option value="DINE_IN">Mesa</option>
                        </Input>
                      </FormGroup>
                      {orderType === 'DINE_IN' && (
                        <FormGroup>
                          <Label>Mesa</Label>
                          <Input
                            type="select"
                            value={tableId}
                            onChange={(e) => setTableId(e.target.value)}
                          >
                            <option value="">Selecione a mesa</option>
                            {tables.map((t) => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                          </Input>
                        </FormGroup>
                      )}
                      {orderType === 'DELIVERY' && (
                        <FormGroup>
                          <Label>Taxa de Entrega</Label>
                          <Input
                            type="text"
                            value={deliveryInput}
                            onChange={(e) => {
                              const cents = parseBRLToCents(e.target.value);
                              setDeliveryCents(cents);
                              setDeliveryInput(formatCentsToBRL(cents));
                            }}
                            placeholder="R$ 0,00"
                          />
                        </FormGroup>
                      )}
                      <FormGroup>
                        <Label>Observação do pedido</Label>
                        <Input
                          type="textarea"
                          rows={3}
                          maxLength={500}
                          value={orderNote}
                          onChange={(e) => setOrderNote(e.target.value.slice(0, 500))}
                          placeholder="Ex.: sem cebola, interfone 21, deixar na portaria"
                          disabled={!canEdit}
                        />
                        <small className="text-muted">{orderNote.length}/500</small>
                      </FormGroup>
                    </CardBody>
                  </Card>

                  {orderType === 'DELIVERY' && (
                  <Card className="card-plain">
                    <CardHeader>
                      <CardTitle tag="h5">Endereço de Entrega</CardTitle>
                    </CardHeader>
                    <CardBody>
                      <FormGroup>
                        <Label>CEP *</Label>
                        <Input
                          type="text"
                          value={zipCode}
                          onChange={(e) => handleCepChange(e.target.value)}
                          placeholder="00000-000"
                          maxLength={9}
                          disabled={loadingCep}
                        />
                        {loadingCep && (
                          <small className="text-muted">Buscando endereço...</small>
                        )}
                      </FormGroup>
                      
                      <FormGroup>
                        <Label>Rua</Label>
                        <Input
                          type="text"
                          value={street}
                          onChange={(e) => setStreet(e.target.value)}
                          placeholder="Nome da rua"
                        />
                      </FormGroup>
                      
                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Número *</Label>
                            <Input
                              type="text"
                              value={number}
                              onChange={(e) => setNumber(e.target.value)}
                              placeholder="123"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Complemento</Label>
                            <Input
                              type="text"
                              value={complement}
                              onChange={(e) => setComplement(e.target.value)}
                              placeholder="Apto 101"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      
                      <FormGroup>
                        <Label>Bairro</Label>
                        <Input
                          type="text"
                          value={neighborhood}
                          onChange={(e) => setNeighborhood(e.target.value)}
                          placeholder="Nome do bairro"
                        />
                      </FormGroup>
                      
                      <Row>
                        <Col md={8}>
                          <FormGroup>
                            <Label>Cidade</Label>
                            <Input
                              type="text"
                              value={city}
                              onChange={(e) => setCity(e.target.value)}
                              placeholder="São Paulo"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label>UF</Label>
                            <Input
                              type="text"
                              value={state}
                              onChange={(e) => setState(e.target.value.toUpperCase())}
                              placeholder="SP"
                              maxLength={2}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                  )}
                </Col>

                {/* Menu Items */}
                <Col lg="4" md="6">
                  <Card className="card-plain">
                    <CardHeader>
                      <CardTitle tag="h5">Cardápio</CardTitle>
                    </CardHeader>
                    <CardBody style={{ maxHeight: '600px', overflowY: 'auto' }}>
                      {loadingMenu && <p className="text-muted">Carregando cardápio...</p>}
                      {!loadingMenu && menu.length === 0 && (
                        <p className="text-muted">Nenhum item disponível</p>
                      )}
                      {menu.map((category) => {
                        const activeItems = category.items?.filter(item => item.active) || [];
                        if (activeItems.length === 0) return null;
                        
                        return (
                          <div key={category.id} style={{ marginBottom: 12 }}>
                            <div
                              onClick={() => toggleCategory(category.id)}
                              style={{
                                cursor: 'pointer',
                                padding: '12px 16px',
                                backgroundColor: openCategories[category.id] ? '#1d8cf8' : '#344675',
                                borderRadius: 6,
                                transition: 'all 0.2s',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                color: '#fff',
                              }}
                            >
                              <h6 style={{ fontWeight: 600, margin: 0, color: '#fff' }}>
                                {category.name}
                              </h6>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 12, color: '#fff', opacity: 0.9 }}>
                                  {activeItems.length} {activeItems.length === 1 ? 'item' : 'itens'}
                                </span>
                                <i 
                                  className={`tim-icons ${openCategories[category.id] ? 'icon-minimal-up' : 'icon-minimal-down'}`}
                                  style={{ fontSize: 12 }}
                                />
                              </div>
                            </div>
                            <Collapse isOpen={openCategories[category.id]}>
                              <div style={{ marginTop: 8 }}>
                                {activeItems.map((item) => (
                                  <div
                                    key={item.id}
                                    onClick={() => handleSelectItem(item)}
                                    style={{
                                      cursor: 'pointer',
                                      borderRadius: 6,
                                      transition: 'all 0.2s',
                                      padding: '12px',
                                      marginBottom: 8,
                                      backgroundColor: selectedItem?.id === item.id ? '#1d8cf8' : '#f4f4f4',
                                      color: selectedItem?.id === item.id ? '#fff' : '#344675',
                                    }}
                                  >
                                    <div className="d-flex justify-content-between align-items-start">
                                      <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 600 }}>{item.name}</div>
                                        {item.description && (
                                          <div style={{ 
                                            fontSize: 12, 
                                            marginTop: 4,
                                            opacity: 0.8
                                          }}>
                                            {item.description.substring(0, 60)}
                                            {item.description.length > 60 ? '...' : ''}
                                          </div>
                                        )}
                                      </div>
                                      <div style={{ fontWeight: 600, marginLeft: 10 }}>
                                        {formatItemPrice(item)}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </Collapse>
                          </div>
                        );
                      })}
                    </CardBody>
                  </Card>

                  {selectedItem && (
                    <Card className="card-plain">
                      <CardHeader>
                        <CardTitle tag="h5">Personalizar Item</CardTitle>
                      </CardHeader>
                      <CardBody>
                        <p style={{ fontWeight: 600, marginBottom: 15 }}>{selectedItem.name}</p>

                        {itemHasMultipleSizes(selectedItem) && (
                          <FormGroup>
                            <Label>Medida</Label>
                            {getActiveSizes(selectedItem).map((size) => (
                              <div
                                key={size.id}
                                className="d-flex align-items-center p-2 mb-2 bg-secondary"
                                onClick={() => setSelectedSize(size)}
                                style={{ cursor: 'pointer', borderRadius: 4 }}
                              >
                                <Input
                                  type="radio"
                                  checked={selectedSize?.id === size.id}
                                  onChange={() => {}}
                                  style={{ marginRight: 8 }}
                                />
                                <span>
                                  {size.name} ({formatCentsToBRL(size.priceCents)})
                                </span>
                              </div>
                            ))}
                          </FormGroup>
                        )}

                        <FormGroup>
                          <Label>Quantidade</Label>
                          <Input
                            type="number"
                            min={1}
                            value={quantity}
                            onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                          />
                        </FormGroup>

                        {selectedItem.variants && selectedItem.variants.length > 0 && (
                          <FormGroup>
                            <Label>Acréscimos</Label>
                            {selectedItem.variants.map((variant) => (
                              <div
                                key={variant.id}
                                className="d-flex align-items-center p-2 mb-2 bg-secondary"
                                onClick={() => handleVariantToggle(variant)}
                                style={{
                                  cursor: 'pointer',
                                  borderRadius: 4,
                                }}
                              >
                                <Input
                                  type="checkbox"
                                  checked={selectedVariants.some(v => v.id === variant.id)}
                                  onChange={() => {}}
                                  style={{ marginRight: 8 }}
                                />
                                <span>
                                  {variant.name} (+{formatCentsToBRL(variant.extraCents)})
                                </span>
                              </div>
                            ))}
                          </FormGroup>
                        )}

                        <FormGroup>
                          <Label>Observação</Label>
                          <Input
                            type="textarea"
                            rows={2}
                            value={itemNote}
                            onChange={(e) => setItemNote(e.target.value)}
                            placeholder="Ex: Sem cebola"
                          />
                        </FormGroup>

                        <div className="d-flex" style={{ gap: 8 }}>
                          <Button 
                            color="secondary" 
                            style={{ flex: 1 }} 
                            onClick={clearItemSelection}
                          >
                            Cancelar
                          </Button>
                          <Button 
                            color="primary" 
                            style={{ flex: 1 }} 
                            onClick={handleAddToCart}
                            disabled={!selectedSize}
                          >
                            Adicionar
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  )}
                </Col>

                {/* Cart */}
                <Col lg="4" md="6">
                  <Card className="card-plain">
                    <CardHeader>
                      <CardTitle tag="h5">
                        Carrinho ({cart.length} {cart.length === 1 ? 'item' : 'itens'})
                      </CardTitle>
                    </CardHeader>
                    <CardBody style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      {cart.length === 0 && (
                        <p className="text-muted text-center py-4">
                          Carrinho vazio. Adicione itens do cardápio.
                        </p>
                      )}
                      {cart.map((item) => (
                        <div key={item.id} className="p-3 mb-2 bg-secondary" style={{ borderRadius: 6 }}>
                          <div className="d-flex justify-content-between align-items-start">
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600 }}>
                                {item.quantity}x {item.name}
                                {item.sizeName && (
                                  <span className="text-muted"> · {item.sizeName}</span>
                                )}
                              </div>
                              {item.variants.length > 0 && (
                                <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                                  {formatVariants(item.variants)}
                                </div>
                              )}
                              {item.note && (
                                <div className="text-muted" style={{ fontSize: 12, fontStyle: 'italic', marginTop: 4 }}>
                                  Obs: {item.note}
                                </div>
                              )}
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                                {formatCentsToBRL(item.lineTotalCents)}
                              </div>
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => handleRemoveFromCart(item.id)}
                                style={{ padding: '4px 8px', fontSize: 11 }}
                              >
                                Remover
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </CardBody>
                  </Card>

                  <Card className="card-plain">
                    <CardBody>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Subtotal:</span>
                        <strong>{formatCentsToBRL(calculateSubtotal())}</strong>
                      </div>
                      <div className="d-flex justify-content-between mb-2">
                        <span>Entrega:</span>
                        <strong>{formatCentsToBRL(deliveryCents)}</strong>
                      </div>
                      <hr />
                      <div className="d-flex justify-content-between" style={{ fontSize: 18, fontWeight: 700 }}>
                        <span>Total:</span>
                        <span className="text-primary">{formatCentsToBRL(calculateTotal())}</span>
                      </div>
                      
                      <Button
                        color="primary"
                        block
                        size="lg"
                        onClick={handleSubmit}
                        disabled={saving || cart.length === 0 || !canEdit}
                        className="mt-4"
                      >
                        {saving ? 'Salvando Alterações...' : 'Salvar Alterações'}
                      </Button>
                      {!canEdit && (
                        <small className="text-danger d-block mt-2 text-center">
                          Edição bloqueada: o pedido tem mais de 48 horas.
                        </small>
                      )}
                    </CardBody>
                  </Card>

                  {(createdAt || activities.length > 0) && (
                    <Card className="card-plain">
                      <CardBody>
                        <div style={{ marginTop: 4, paddingTop: 12, borderTop: '1px solid #444' }}>
                          <h5 style={{ marginBottom: 8, fontSize: 15, fontWeight: 600 }}>
                            <i className="tim-icons icon-time-alarm" /> Informações Adicionais
                          </h5>
                          <div style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>
                            <div style={{ marginBottom: 4 }}>
                              <strong>Pedido #:</strong> {orderId}
                            </div>
                            {createdAt && (
                              <div style={{ marginBottom: 4 }}>
                                <strong>Criado em:</strong><br/>
                                {new Date(createdAt).toLocaleString('pt-BR', {
                                  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
                                })}
                              </div>
                            )}
                            {orderSource && (
                              <div style={{ marginBottom: 4 }}>
                                <strong>Origem:</strong> {getOrderSourceLabel(orderSource)}
                              </div>
                            )}
                            {updatedAt && updatedAt !== createdAt && (
                              <div style={{ marginBottom: 4 }}>
                                <strong>Atualizado em:</strong><br/>
                                {new Date(updatedAt).toLocaleString('pt-BR', {
                                  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                        {activities.length > 0 && (
                        <div style={{ marginTop: 4, paddingTop: 12, borderTop: '1px solid #444' }}>
                          <h5 style={{ marginBottom: 8, fontSize: 15, fontWeight: 600 }}>
                            <i className="tim-icons icon-notes" /> Atividade
                          </h5>
                          <div style={{ fontSize: 12, color: '#999' }}>
                            {activities.map((a) => (
                              <div key={a.id} style={{ marginBottom: 6, lineHeight: 1.4 }}>
                                <strong style={{ color: '#bbb' }}>{a.user?.name || 'Sistema'}</strong>{' '}
                                {ACTIVITY_LABELS[a.action] || a.action}
                                {a.detail ? ` · ${a.detail}` : ''}
                                <div style={{ color: '#666' }}>
                                  {new Date(a.createdAt).toLocaleString('pt-BR', {
                                    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        )}
                      </CardBody>
                    </Card>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ConfirmDeleteModal
        isOpen={showConfirmExit}
        toggle={() => setShowConfirmExit(false)}
        title="Sair sem salvar?"
        message="Deseja realmente sair e descartar as alterações?"
        btnConfirm="Sair"
        btnCancel="Continuar editando"
        onConfirm={confirmExit}
      />
    </div>
  );
};

export default EditOrder;
